import { useMemo } from 'react';

import { Button, CheckBox } from '@iwoca/orion';
import { RootState } from '@iwProvider/configureStore';
import { useStripe } from '@stripe/react-stripe-js';
import { Stripe, StripeCardNumberElement } from '@stripe/stripe-js';
import { FieldPath, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import * as yup from 'yup';

import styles from '../LinkDebitCard.module.css';
import { BillingPostcodeField } from './formFields/BillingPostcodeField';
import { CardholderNameField } from './formFields/CardholderNameField';
import { CardNumberField } from './formFields/CardNumberField';
import { CVVField } from './formFields/CVVField';
import { ExpiryDateField } from './formFields/ExpiryDateField';
import { createLinkDebitCardSchema } from './formValidation/linkDebitCardSchema';
import { getCountry } from '../../../iwIntlProvider/CountryProvider';
import {
    HANDLE_STRIPE_FIELD_CHANGE,
    SAVE_CARD_NUMBER_ELEMENT
} from '../../modals/addPaymentMethodModal/AddPaymentMethodModal.actions';

export type TFormValues = {
    billingPostcode: string;
    cardholderName: string;
    repaymentRole: boolean;
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
    saveCardNumberElement: (cardNumberElement: StripeCardNumberElement) =>
        dispatch({
            type: SAVE_CARD_NUMBER_ELEMENT,
            cardNumberElement
        }),
    handleFieldChange: (field: string, response: any) =>
        dispatch({
            type: HANDLE_STRIPE_FIELD_CHANGE,
            field: field,
            response: response
        })
});

const mapStateToProps = (state: RootState) => ({
    ...state.addPaymentMethodModal
});

type PropsFromRedux = ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

type OwnProps = {
    linkingDebitCard?: boolean;
    showDefaultPaymentMethodCheckbox?: boolean;
    useSectionGridLayout?: boolean;
    onSuccess: (
        stripe: Stripe,
        repaymentRole: string | null,
        billingPostcode: string,
        cardholderName: string
    ) => void;
};

const StripeLinkDebitCardFormBase = ({
    invalidCardType,
    invalidCard,
    invalidNumber,
    invalidCVV,
    invalidExpiryDate,
    linkingDebitCard,
    registerCardStatus,
    onSuccess,
    saveCardNumberElement,
    handleFieldChange,
    showDefaultPaymentMethodCheckbox = false,
    useSectionGridLayout = false
}: OwnProps & PropsFromRedux) => {
    const intl = useIntl();
    const stripe = useStripe();
    const country = getCountry();

    const schema = useMemo(
        () => createLinkDebitCardSchema(intl, country),
        [intl, country]
    );

    const {
        register,
        getValues,
        setError,
        clearErrors,
        formState: { errors }
    } = useForm<TFormValues>({
        defaultValues: {
            billingPostcode: '',
            cardholderName: '',
            // Defaults to true so the dispatched action carries
            // repaymentRole: 'primary' when the checkbox is hidden.
            repaymentRole: true
        }
    });

    const handleChange = (field: string) => (response: any) => {
        handleFieldChange(field, response);
    };

    const handleSave = () => {
        if (!stripe) return;
        const values = getValues();
        clearErrors();
        try {
            schema.validateSync(values, { abortEarly: false });
            onSuccess(
                stripe,
                values.repaymentRole ? 'primary' : null,
                values.billingPostcode,
                values.cardholderName
            );
        } catch (err) {
            if (err instanceof yup.ValidationError) {
                for (const inner of err.inner) {
                    if (inner.path) {
                        setError(inner.path as FieldPath<TFormValues>, {
                            type: inner.type ?? 'validate',
                            message: inner.message
                        });
                    }
                }
            }
        }
    };

    return (
        <form
            data-testid="link-debit-card-modal"
            className={styles.LinkDebitCardForm}
            id="payment-form"
            onSubmit={(e) => {
                e.preventDefault();
                handleSave();
            }}
        >
            <div
                className={
                    useSectionGridLayout
                        ? 'grid gap-l pt-xl min-m:grid-cols-3'
                        : styles.topRow
                }
            >
                <CardNumberField
                    onReady={(element) => saveCardNumberElement(element)}
                    error={{
                        invalidNumber,
                        invalidCard,
                        invalidCardType
                    }}
                    onChange={handleChange('cardNumber')}
                />
                <ExpiryDateField
                    error={invalidExpiryDate}
                    onChange={handleChange('expiryDate')}
                />
                <CVVField error={invalidCVV} onChange={handleChange('CVV')} />
                <BillingPostcodeField
                    formRegister={register}
                    error={errors.billingPostcode?.message}
                />
                <CardholderNameField
                    formRegister={register}
                    error={errors.cardholderName?.message}
                />
            </div>

            {showDefaultPaymentMethodCheckbox && (
                <div className="mb-2xl">
                    <CheckBox
                        label={intl.formatMessage({
                            id: '_.link_debit_card.repayment_role_label',
                            defaultMessage:
                                'Use as default payment method for automatic repayments'
                        })}
                        {...register('repaymentRole')}
                    />
                </div>
            )}

            {registerCardStatus ? (
                <FormattedMessage
                    id="_.link_debit_card.successful_link"
                    defaultMessage="Thanks! You've successfully linked your card! Continue to the next step"
                />
            ) : null}

            <div className="mt-xl flex w-full flex-col items-center gap-m">
                <Button
                    type="submit"
                    className="w-full max-w-[300px]"
                    loading={linkingDebitCard}
                >
                    <FormattedMessage
                        id="_.link_debit_card.link_card"
                        defaultMessage="Add your card"
                    />
                </Button>
            </div>
        </form>
    );
};

export const StripeLinkDebitCardForm = connect(
    mapStateToProps,
    mapDispatchToProps
)(StripeLinkDebitCardFormBase);
